import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import hostelService from '../../services/hostelService';
import { FormSelect } from '../../components/finance';
import { Plus, MagnifyingGlass, PencilSimple, Trash, CircleNotch, ArrowLeft, ClipboardText, X } from '@phosphor-icons/react';
import DialogModal from '../../components/DialogModal';
import './hostel.css';

function buildDeleteImpactMessage(impact, noneMessage) {
  let message;
  if (impact.total_to_be_deleted > 0) {
    const deletedLines = Object.entries(impact.will_be_deleted)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message = `This will permanently delete ${impact.total_to_be_deleted} record(s):\n${deletedLines}\n\nThis cannot be undone.`;
  } else {
    message = noneMessage;
  }
  if (Object.keys(impact.will_be_preserved || {}).length > 0) {
    const preservedLines = Object.entries(impact.will_be_preserved)
      .map(([label, count]) => `- ${count} ${label}`)
      .join('\n');
    message += `\n\nThese will be kept, just unlinked:\n${preservedLines}`;
  }
  return message;
}

function RoomsManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [hostels, setHostels] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [hostelFilter, setHostelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, roomId: null, message: '', loading: false });
  const [formData, setFormData] = useState({
    hostel_id: '',
    room_number: '',
    room_type: 'double',
    capacity: 2,
    floor: 1,
    has_bathroom: true,
    has_ac: false,
    has_heater: true,
    has_desk: true,
    has_bed: true,
    status: 'vacant'
  });
  const [editingId, setEditingId] = useState(null);

  // Inventory / condition log modal (per room)
  const [inventoryModal, setInventoryModal] = useState({ isOpen: false, room: null });
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryForm, setInventoryForm] = useState({ item_name: '', condition: 'good', last_checked_date: '', notes: '' });
  const [editingInventoryId, setEditingInventoryId] = useState(null);
  const [savingInventory, setSavingInventory] = useState(false);
  const [deleteInventoryDialog, setDeleteInventoryDialog] = useState({ isOpen: false, itemId: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [roomsData, hostelsData] = await Promise.all([
        hostelService.listRooms(),
        hostelService.listHostels()
      ]);

      const roomsList = Array.isArray(roomsData) ? roomsData : roomsData.rooms || [];
      const hostelsList = Array.isArray(hostelsData) ? hostelsData : hostelsData.hostels || [];
      
      setRooms(roomsList);
      setHostels(hostelsList);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load rooms and hostels');
    } finally {
      setLoading(false);
    }
  };

  const filterRooms = React.useCallback(() => {
    let filtered = rooms;

    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.room_number.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (hostelFilter !== 'all') {
      filtered = filtered.filter(r => r.hostel_id === hostelFilter);
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.room_type === typeFilter);
    }

    setFilteredRooms(filtered);
  }, [rooms, searchTerm, hostelFilter, typeFilter]);

  useEffect(() => {
    filterRooms();
  }, [rooms, searchTerm, hostelFilter, typeFilter, filterRooms]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingId) {
        await hostelService.updateRoom(editingId, formData);
        toast.success('Room updated successfully');
      } else {
        await hostelService.createRoom(formData);
        toast.success('Room created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving room:', error);
      toast.error(editingId ? 'Failed to update room' : 'Failed to create room');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (room) => {
    setFormData(room);
    setEditingId(room.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeleteDialog({ isOpen: true, roomId: id, message: 'Checking what this will affect...', loading: true });
    try {
      const impact = await hostelService.getRoomDeleteImpact(id);
      const message = buildDeleteImpactMessage(impact, 'No allocations, maintenance records, or complaints are tied to this room yet — safe to delete.');
      setDeleteDialog({ isOpen: true, roomId: id, message, loading: false });
    } catch (error) {
      console.error('Error fetching delete impact:', error);
      setDeleteDialog({ isOpen: true, roomId: id, message: 'Delete this room? This cannot be undone.', loading: false });
    }
  };

  const confirmDeleteRoom = async () => {
    const id = deleteDialog.roomId;
    setDeleteDialog({ isOpen: false, roomId: null, message: '', loading: false });
    try {
      await hostelService.deleteRoom(id);
      toast.success('Room deleted successfully');
      loadData();
    } catch (error) {
      console.error('Error deleting room:', error);
      toast.error('Failed to delete room');
    }
  };

  const resetForm = () => {
    setFormData({
      hostel_id: '',
      room_number: '',
      room_type: 'double',
      capacity: 2,
      floor: 1,
      has_bathroom: true,
      has_ac: false,
      has_heater: true,
      has_desk: true,
      has_bed: true,
      status: 'vacant'
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    resetForm();
  };

  const resetInventoryForm = () => {
    setInventoryForm({ item_name: '', condition: 'good', last_checked_date: '', notes: '' });
    setEditingInventoryId(null);
  };

  const openInventoryModal = async (room) => {
    setInventoryModal({ isOpen: true, room });
    resetInventoryForm();
    setLoadingInventory(true);
    try {
      const data = await hostelService.getRoomInventory(room.id);
      setInventoryItems(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error('Error loading room inventory:', error);
      toast.error('Failed to load room inventory');
      setInventoryItems([]);
    } finally {
      setLoadingInventory(false);
    }
  };

  const closeInventoryModal = () => {
    setInventoryModal({ isOpen: false, room: null });
    setInventoryItems([]);
    resetInventoryForm();
  };

  const handleInventorySubmit = async (e) => {
    e.preventDefault();
    if (!inventoryModal.room) return;
    setSavingInventory(true);
    try {
      if (editingInventoryId) {
        await hostelService.updateRoomInventoryItem(editingInventoryId, inventoryForm);
        toast.success('Inventory item updated');
      } else {
        await hostelService.createRoomInventoryItem(inventoryModal.room.id, inventoryForm);
        toast.success('Inventory item added');
      }
      const data = await hostelService.getRoomInventory(inventoryModal.room.id);
      setInventoryItems(Array.isArray(data) ? data : data.items || []);
      resetInventoryForm();
    } catch (error) {
      console.error('Error saving inventory item:', error);
      toast.error('Failed to save inventory item');
    } finally {
      setSavingInventory(false);
    }
  };

  const handleEditInventoryItem = (item) => {
    setInventoryForm({
      item_name: item.item_name,
      condition: item.condition,
      last_checked_date: item.last_checked_date,
      notes: item.notes || '',
    });
    setEditingInventoryId(item.id);
  };

  const confirmDeleteInventoryItem = async () => {
    const itemId = deleteInventoryDialog.itemId;
    setDeleteInventoryDialog({ isOpen: false, itemId: null });
    try {
      await hostelService.deleteRoomInventoryItem(itemId);
      toast.success('Inventory item deleted');
      setInventoryItems((prev) => prev.filter((i) => i.id !== itemId));
    } catch (error) {
      console.error('Error deleting inventory item:', error);
      toast.error('Failed to delete inventory item');
    }
  };

  const getHostelName = (hostelId) => {
    const hostel = hostels.find(h => h.id === hostelId);
    return hostel ? hostel.hostel_name : 'Unknown';
  };

  if (loading) {
    return <Layout><div className="loading">Loading rooms...</div></Layout>;
  }

  return (
    <Layout>
      <div className="page hostel-page">
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-icon" onClick={() => navigate('/hostel')} title="Back to Hostel">
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>Rooms Management</h1>
              <p>Create and manage hostel rooms</p>
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
            }}
          >
            <Plus size={20} />
            Add Room
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search rooms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={hostelFilter}
            onChange={(e) => setHostelFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Hostels</option>
            {hostels.map(h => (
              <option key={h.id} value={h.id}>{h.hostel_name}</option>
            ))}
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="single">Single</option>
            <option value="double">Double</option>
            <option value="triple">Triple</option>
          </select>
        </div>

        {showForm && (
          <div className="form-container">
            <div className="form-card">
              <h2>{editingId ? 'Edit Room' : 'Add New Room'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Hostel"
                      name="hostel_id"
                      value={formData.hostel_id}
                      onChange={(e) => setFormData({ ...formData, hostel_id: e.target.value })}
                      required
                      disabled={submitting}
                      options={hostels.map(h => ({
                        value: h.id,
                        label: h.hostel_name
                      }))}
                      placeholder="Select Hostel"
                    />
                  </div>

                  <div className="form-group">
                    <label>Room Number *</label>
                    <input
                      type="text"
                      value={formData.room_number}
                      onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                      required
                      disabled={submitting}
                      placeholder="e.g., A01"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <FormSelect
                      label="Room Type"
                      name="room_type"
                      value={formData.room_type}
                      onChange={(e) => setFormData({ ...formData, room_type: e.target.value })}
                      required
                      disabled={submitting}
                      options={[
                        { value: 'single', label: 'Single' },
                        { value: 'double', label: 'Double' },
                        { value: 'triple', label: 'Triple' }
                      ]}
                    />
                  </div>

                  <div className="form-group">
                    <label>Capacity *</label>
                    <input
                      type="number"
                      value={formData.capacity}
                      onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
                      required
                      disabled={submitting}
                      min="1"
                    />
                  </div>

                  <div className="form-group">
                    <label>Floor *</label>
                    <input
                      type="number"
                      value={formData.floor}
                      onChange={(e) => setFormData({ ...formData, floor: parseInt(e.target.value) })}
                      required
                      disabled={submitting}
                      min="1"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Amenities</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_bathroom}
                        onChange={(e) => setFormData({ ...formData, has_bathroom: e.target.checked })}
                        disabled={submitting}
                      />
                      Bathroom
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_ac}
                        onChange={(e) => setFormData({ ...formData, has_ac: e.target.checked })}
                        disabled={submitting}
                      />
                      Air Conditioning
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_heater}
                        onChange={(e) => setFormData({ ...formData, has_heater: e.target.checked })}
                        disabled={submitting}
                      />
                      Heater
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_desk}
                        onChange={(e) => setFormData({ ...formData, has_desk: e.target.checked })}
                        disabled={submitting}
                      />
                      Desk
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.has_bed}
                        onChange={(e) => setFormData({ ...formData, has_bed: e.target.checked })}
                        disabled={submitting}
                      />
                      Bed
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <FormSelect
                    label="Status"
                    name="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    required
                    disabled={submitting}
                    options={[
                      { value: 'vacant', label: 'Vacant' },
                      { value: 'occupied', label: 'Occupied' },
                      { value: 'maintenance', label: 'Maintenance' }
                    ]}
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={submitting}>
                    {submitting ? (
                      <span className="button-loading">
                        <CircleNotch size={16} weight="fill" className="spinner" />
                        Saving...
                      </span>
                    ) : editingId ? 'Update Room' : 'Create Room'}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={submitting}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="table-container">
          {filteredRooms.length === 0 ? (
            <div className="empty-state">
              <p>No rooms found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Room Number</th>
                  <th>Hostel</th>
                  <th>Type</th>
                  <th>Capacity</th>
                  <th>Floor</th>
                  <th>Amenities</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRooms.map((room) => (
                  <tr key={room.id}>
                    <td>{room.room_number}</td>
                    <td>{getHostelName(room.hostel_id)}</td>
                    <td className="text-capitalize">{room.room_type}</td>
                    <td>{room.capacity}</td>
                    <td>{room.floor}</td>
                    <td>
                      {[
                        room.has_bathroom && 'Bath',
                        room.has_ac && 'AC',
                        room.has_heater && 'Heat',
                        room.has_desk && 'Desk',
                        room.has_bed && 'Bed'
                      ].filter(Boolean).join(', ')}
                    </td>
                    <td>
                      <span className={`badge badge-${room.status === 'vacant' ? 'success' : room.status === 'occupied' ? 'info' : 'warning'}`}>
                        {room.status}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="btn-icon"
                          onClick={() => openInventoryModal(room)}
                          title="Inventory / Condition Log"
                        >
                          <ClipboardText size={18} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleEdit(room)}
                          title="Edit"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(room.id)}
                          title="Delete"
                        >
                          <Trash size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <DialogModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, roomId: null, message: '', loading: false })}
        title="Delete Room"
        message={deleteDialog.message}
        type="warning"
        isLoading={deleteDialog.loading}
        disableCloseWhenLoading={false}
        primaryAction={deleteDialog.loading ? null : { label: 'Delete', onClick: confirmDeleteRoom }}
        secondaryAction={deleteDialog.loading ? null : { label: 'Cancel', onClick: () => {} }}
      />

      {inventoryModal.isOpen && (
        <div className="modal-overlay" onClick={closeInventoryModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <h2>Inventory — Room {inventoryModal.room?.room_number}</h2>
              <button type="button" className="btn-close" onClick={closeInventoryModal} aria-label="Close">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleInventorySubmit} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 16 }}>
              <div className="form-group" style={{ flex: '1 1 140px', margin: 0 }}>
                <label>Item</label>
                <input
                  type="text"
                  value={inventoryForm.item_name}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, item_name: e.target.value })}
                  placeholder="e.g., Mattress"
                  required
                  disabled={savingInventory}
                />
              </div>
              <div className="form-group" style={{ flex: '1 1 110px', margin: 0 }}>
                <label>Condition</label>
                <select
                  value={inventoryForm.condition}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, condition: e.target.value })}
                  disabled={savingInventory}
                >
                  <option value="new">New</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="damaged">Damaged</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: '1 1 140px', margin: 0 }}>
                <label>Last Checked</label>
                <input
                  type="date"
                  value={inventoryForm.last_checked_date}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, last_checked_date: e.target.value })}
                  required
                  disabled={savingInventory}
                />
              </div>
              <div className="form-group" style={{ flex: '1 1 160px', margin: 0 }}>
                <label>Notes</label>
                <input
                  type="text"
                  value={inventoryForm.notes}
                  onChange={(e) => setInventoryForm({ ...inventoryForm, notes: e.target.value })}
                  placeholder="Optional"
                  disabled={savingInventory}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={savingInventory}>
                  {savingInventory ? 'Saving...' : editingInventoryId ? 'Update' : 'Add'}
                </button>
                {editingInventoryId && (
                  <button type="button" className="btn btn-secondary" onClick={resetInventoryForm} disabled={savingInventory}>
                    Cancel
                  </button>
                )}
              </div>
            </form>

            {loadingInventory ? (
              <div className="loading">Loading inventory...</div>
            ) : inventoryItems.length === 0 ? (
              <div className="empty-state"><p>No inventory items logged for this room yet</p></div>
            ) : (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Condition</th>
                    <th>Last Checked</th>
                    <th>Notes</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {inventoryItems.map((item) => (
                    <tr key={item.id}>
                      <td>{item.item_name}</td>
                      <td className="text-capitalize">{item.condition}</td>
                      <td>{item.last_checked_date}</td>
                      <td>{item.notes || '-'}</td>
                      <td>
                        <div className="actions">
                          <button className="btn-icon" onClick={() => handleEditInventoryItem(item)} title="Edit">
                            <PencilSimple size={16} />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => setDeleteInventoryDialog({ isOpen: true, itemId: item.id })}
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      <DialogModal
        isOpen={deleteInventoryDialog.isOpen}
        onClose={() => setDeleteInventoryDialog({ isOpen: false, itemId: null })}
        title="Delete Inventory Item"
        message="Delete this inventory item? This cannot be undone."
        type="warning"
        primaryAction={{ label: 'Delete', onClick: confirmDeleteInventoryItem }}
        secondaryAction={{ label: 'Cancel', onClick: () => {} }}
      />
    </Layout>
  );
}

export default RoomsManagementPage;
